import './claim-entity.detail.overview.scss';
import './common.scss';

import {
  AliasType,
  Claim,
  ContactType,
  GetClaimDetailsQuery,
  getPaginatedNodes,
  useClaimQuery,
} from '@converge/graphql';
import { Box, Typography } from '@mui/material';
import { ConvergeButton, ErrorPageLayout } from '../../../presentation';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';

import { ClaimEntityPageContainer } from './claim-entity.page.container';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { formatDollarAmount } from '@converge/utils';
import { getEntityTypeLabel } from '../utils';
import { styled } from '@mui/material/styles';
import { useConvergeSnackBar } from '../../../contexts';
import { useOutletContext } from 'react-router-dom';
import { useState } from 'react';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
  marginBottom: '16px',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} sx={{ flexDirection: 'row' }} />
))(({ theme }) => ({
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
}));

const ContactCard = styled(Box)(({ theme }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  paddingBottom: '10px',
  width: 'calc(28.33% - 16px)',
  minWidth: '300px',
  backgroundColor: '#fff',
}));

export interface IClaimOutletProps {
  insuredUuid: string;
}
export interface IProps {
  claimEntity: Claim;
  claimUuid: string;
  claimSubmissionDetails?: GetClaimDetailsQuery;
  isNotActive: boolean;
}

export interface IDocumentButtonProps {
  claimUuid: string;
  className?: string;
}
export const DocumentsButton = ({
  claimUuid,
  className,
}: IDocumentButtonProps) => {
  const { data: claimData, loading: documentsLinkLoading } = useClaimQuery({
    claimUuid,
  });
  const { notify } = useConvergeSnackBar();
  return (
    <>
      {claimData ? (
        <ConvergeButton
          sx={{ margin: '20px' }}
          className={className}
          onClick={() => {
            window.open(claimData?.claims.edges[0].node.documentLink, '_blank');
          }}
          data-cy="claim-documents-button"
          label={'Documents'}
          loading={documentsLinkLoading}
        />
      ) : (
        notify('there is no document folder', { variant: 'error' })
      )}
    </>
  );
};

export const ClaimEntityDetailOverview = () => {
  const { claimEntity, claimUuid, claimSubmissionDetails } =
    useOutletContext<IProps>();

  const capacityProvider =
    claimSubmissionDetails?.submissions?.edges[0]?.node?.capacityProviders;
  const brokerageEntity =
    claimSubmissionDetails?.submissions?.edges[0]?.node?.brokerEntity?.node?.aliases?.edges?.filter(
      (item) => item?.node?.type === 'LEGAL_NAME'
    )[0]?.node;
  const broker =
    claimSubmissionDetails?.submissions?.edges[0]?.node?.persons?.edges.filter(
      (item) => item.node.personType?.type === 'AGENCY_CONTACT'
    )[0]?.node;

  if (!claimEntity) {
    return (
      <ErrorPageLayout
        message="No claim data found"
        messageId="insured.detail.page.claimEntityNotFound"
      />
    );
  }

  return (
    <ClaimEntityDetailOverviewInner
      data={claimEntity}
      claimUuid={claimUuid}
      capacityProviders={capacityProvider}
      brokerageEntity={brokerageEntity}
      broker={broker}
    />
  );
};

export const ClaimEntityDetailOverviewInner = ({
  data,
  claimUuid,
  capacityProviders,
  brokerageEntity,
  broker,
}: {
  data: Claim;
  claimUuid?: string;
  capacityProviders?: GetClaimDetailsQuery['submissions']['edges'][0]['node']['capacityProviders'];
  brokerageEntity?: GetClaimDetailsQuery['submissions']['edges'][0]['node']['brokerEntity']['node']['aliases']['edges'][0]['node'];
  broker?: GetClaimDetailsQuery['submissions']['edges'][0]['node']['persons']['edges'][0]['node'];
}) => {
  const [isInsuredOpen, setInsuredOpen] = useState(true);
  const [isCompanyOpen, setCompanyOpen] = useState(true);
  const [isFinancialOpen, setFinancialOpen] = useState(true);
  const [isPolicyOpen, setPolicyOpen] = useState(true);

  // const { claimCreateClosingLetter, loading: claimCreateClosingLetterLoading } =
  //   useCreateClosingLetterMutation();

  const handleInsuredAccordion = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setInsuredOpen(newExpanded);
  };

  const handleCompanyAccordion = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setCompanyOpen(newExpanded);
  };

  const handleFinancialAccordion = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setFinancialOpen(newExpanded);
  };

  const handlePolicyAccordion = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPolicyOpen(newExpanded);
  };

  // Extract insured entity data
  const insuredEntity = data?.insuredEntity?.node;
  const insuredAliases = getPaginatedNodes(insuredEntity?.aliases) || [];
  const insuredAddresses = getPaginatedNodes(insuredEntity?.addresses) || [];
  const insuredPersons = getPaginatedNodes(insuredEntity?.persons) || [];
  const fundamentals = getPaginatedNodes(insuredEntity?.fundamentals) || [];
  const industrySectors =
    getPaginatedNodes(insuredEntity?.industrySectors) || [];

  // Extract financial data with fallbacks
  const fundamentalItem = fundamentals.filter(
    (fundamental) => fundamental.primary
  )[0];

  // Define financial values with proper type assertions
  const revenue = fundamentalItem ? fundamentalItem.revenue : undefined;
  const projectedRevenue = fundamentalItem
    ? fundamentalItem.projectedRevenue
    : undefined;
  const foreignRevenue = fundamentalItem
    ? fundamentalItem.foreignRevenue
    : undefined;
  const projectedForeignRevenue = fundamentalItem
    ? fundamentalItem.projectedForeignRevenue
    : undefined;
  const currentRatio = fundamentalItem
    ? fundamentalItem.currentRatio
    : undefined;
  const cashRatio = fundamentalItem ? fundamentalItem.cashRatio : undefined;
  const quickRatio = fundamentalItem ? fundamentalItem.quickRatio : undefined;
  const employees = fundamentalItem ? fundamentalItem.employees : undefined;
  const yearEstablished = fundamentalItem
    ? fundamentalItem.yearEstablished
    : undefined;

  const formatRatioValue = (value?: number | null) =>
    value ? `${value.toFixed(2)}%` : '-%';
  const domains = getPaginatedNodes(insuredEntity?.domains || { edges: [] });
  const domain = domains.length > 0 && domains[0] ? domains[0].domain : null;
  // Get primary insured name and details
  const legalNameAlias = insuredAliases.find(
    (alias) => alias?.primary && alias?.type === AliasType.LegalName
  );
  const legalName = legalNameAlias ? (legalNameAlias.name as string) : '—';

  const tradingNameAlias = insuredAliases.find(
    (alias) => alias?.primary && alias?.type === AliasType.TradingName
  );

  // Get primary contact
  const primaryContact =
    insuredPersons.find(
      (person) => person?.primary && person?.type === 'primary_contact'
    ) ||
    insuredPersons[0] ||
    {};

  // Get contact details
  const contactEmailObj = getPaginatedNodes(primaryContact?.contacts)?.find(
    (contact) => contact?.type === ContactType.Email
  );
  const contactEmail = contactEmailObj
    ? (contactEmailObj.value as string)
    : '-';

  const contactPhoneObj = getPaginatedNodes(primaryContact?.contacts)?.find(
    (contact) => contact?.type === ContactType.Phone
  );
  const contactPhone = contactPhoneObj
    ? (contactPhoneObj.value as string)
    : '-';

  // Get primary address
  const primaryAddress =
    insuredAddresses.find((address) => address.primary) ||
    insuredAddresses[0] ||
    {};

  // Format address
  const formattedAddress = primaryAddress
    ? `${primaryAddress.street1 || ''} ${primaryAddress.street2 || ''}`.trim()
    : '-';

  const formattedCityState = primaryAddress
    ? `${primaryAddress.city || ''}, ${
        getPaginatedNodes(primaryAddress.regions)?.[0]?.name || ''
      }, ${primaryAddress.postalCode || ''}`.trim()
    : '-';

  // Get industry sector
  const industrySectorItem = industrySectors.filter(
    (industrySector) => industrySector.primary
  )[0];
  const industrySector = industrySectorItem
    ? (industrySectorItem.name as string)
    : '-';
  return (
    <ClaimEntityPageContainer title="Insured Overview">
      {/* INSURED INFORMATION Section */}
      <AccordionSection
        title="INSURED INFORMATION"
        expanded={isInsuredOpen}
        onChange={handleInsuredAccordion}
      >
        <CardSection className="flex-column">
          <FieldRow>
            <InfoField
              label="Legal Name"
              value={legalName}
              style={{ flex: '0% 0% 30%' }}
            />
            <InfoField
              label="Entity Reference ID"
              value={insuredEntity?.referenceId}
              style={{ flex: '0 0 19%' }}
            />
            <InfoField
              label="DBA"
              value={tradingNameAlias?.name || '_'}
              style={{ flex: '0 0 10%' }}
            />
            <InfoField label="Domain" value={domain} />
          </FieldRow>

          <FieldRow>
            <InfoField
              label="Contact Name"
              value={`${primaryContact?.firstName || '-'} ${
                primaryContact?.lastName || ''
              }`}
              style={{ flex: '0% 0% 30%' }}
            />
            <InfoField
              label="Contact Email"
              value={contactEmail}
              style={{ flex: '0 0 19%' }}
            />
            <InfoField
              label="Contact Phone"
              value={contactPhone}
              style={{ flex: '0 0 10%' }}
            />
            <InfoField label="" value={'  '} />
          </FieldRow>

          <InfoField
            className="last-element"
            label="Address"
            value={`${formattedAddress} ${formattedCityState}`}
            valueClassName="address-row"
          />
        </CardSection>
      </AccordionSection>

      {/* /* Policy information  */}
      <AccordionSection
        title="POLICY INFORMATION"
        expanded={isPolicyOpen}
        onChange={handlePolicyAccordion}
      >
        <CardSection>
          <FieldRow>
            {/* First Column - Capacity Providers (49% width to match columns 1+2 of other cards) */}
            <div className="claim-field" style={{ flex: '0 0 48%' }}>
              <Typography variant="h4" className="card-title">
                CAPACITY PROVIDER
              </Typography>
              {capacityProviders?.edges?.length ? (
                capacityProviders.edges.map((edge) => (
                  <Typography key={edge.node.uuid} className="card-data-2">
                    {getPaginatedNodes(edge.node.aliases)?.[0]?.name || '-'}
                  </Typography>
                ))
              ) : (
                <Typography variant="h4" className="card-data">
                  -
                </Typography>
              )}
            </div>

            {/* Second Column - Allocations (10% width to align with column 3 of other cards) */}
            <div className="claim-field" style={{ flex: '0 0 10%' }}>
              <Typography variant="h4" className="card-title">
                ALLOCATION
              </Typography>
              {capacityProviders?.edges?.length ? (
                capacityProviders.edges.map((edge) => (
                  <Typography
                    key={`${edge.node.uuid}-allocation`}
                    variant="h4"
                    className="card-data"
                  >
                    {edge.node.quote?.quotaShare
                      ? `${edge.node.quote.quotaShare * 100}%`
                      : '-'}
                  </Typography>
                ))
              ) : (
                <Typography variant="h4" className="card-data">
                  -
                </Typography>
              )}
            </div>

            {/* Third Column - Company Information (remaining width to align with column 4 of other cards) */}
            <div className="claim-field">
              <Box className="brokerage">
                <InfoField
                  label="BROKERAGE"
                  value={brokerageEntity?.name || '-'}
                />
              </Box>
              <InfoField
                className="last-element"
                label="BROKER"
                value={
                  broker?.firstName && broker?.lastName
                    ? `${broker.firstName} ${broker.lastName}`
                    : '-'
                }
              />
            </div>
          </FieldRow>
        </CardSection>
      </AccordionSection>

      {/* COMPANY DETAILS Section */}
      <AccordionSection
        title="COMPANY DETAILS"
        expanded={isCompanyOpen}
        onChange={handleCompanyAccordion}
      >
        <CardSection>
          <FieldRow>
            <InfoField
              label="Industry Sector"
              value={industrySector}
              style={{ flex: '0 0 48%' }}
            />
            <InfoField
              label="Year Established"
              value={yearEstablished}
              style={{ flex: '0 0 10%' }}
            />
            <InfoField
              label="Company Type"
              value={getEntityTypeLabel(insuredEntity?.type || null)}
              className="last-element"
            />
          </FieldRow>
        </CardSection>
      </AccordionSection>

      {/* FINANCIAL DETAILS Section */}
      <AccordionSection
        title="FINANCIAL DETAILS"
        expanded={isFinancialOpen}
        onChange={handleFinancialAccordion}
      >
        <CardSection className="flex-column">
          <FieldRow>
            <InfoField
              label="Current Domestic Revenue"
              value={revenue ? formatDollarAmount(revenue) : '$0.00'}
              style={{ flex: '0% 0% 30%' }}
            />
            <InfoField
              label="Projected Domestic Revenue"
              value={
                projectedRevenue
                  ? formatDollarAmount(projectedRevenue)
                  : '$0.00'
              }
              style={{ flex: '0 0 19%' }}
            />
            <InfoField
              label="Current Foreign Revenue"
              value={
                foreignRevenue ? formatDollarAmount(foreignRevenue) : '$0.00'
              }
              style={{ flex: '0 0 10%' }}
            />
            <InfoField
              label="Projected Foreign Revenue"
              value={
                projectedForeignRevenue
                  ? formatDollarAmount(projectedForeignRevenue)
                  : '$0.00'
              }
            />
          </FieldRow>
          <FieldRow>
            <InfoField
              label="Current Ratio"
              value={formatRatioValue(currentRatio)}
              style={{ flex: '0% 0% 30%' }}
            />
            <InfoField
              label="Cash Ratio"
              value={formatRatioValue(cashRatio)}
              style={{ flex: '0 0 19%' }}
            />
            <InfoField
              label="Quick Ratio"
              value={formatRatioValue(quickRatio)}
              style={{ flex: '0 0 10%' }}
            />
            <InfoField
              label="Employees"
              value={employees || '-'}
              className="last-element"
            />
          </FieldRow>
        </CardSection>
      </AccordionSection>

      {/* Footer */}
    </ClaimEntityPageContainer>
  );
};

interface InfoFieldProps {
  label: string;
  value: React.ReactNode;
  className?: string;
  valueClassName?: string;
  style?: React.CSSProperties;
}

/**
 * InfoField - A reusable component for displaying a label-value pair
 */
const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  className,
  valueClassName,
  style,
}) => (
  <div className={`claim-field ${className || ''}`} style={style}>
    <Typography variant="h4" className="card-title">
      {label}
    </Typography>
    <Typography
      variant="h4"
      className={`card-data last-item-card ${valueClassName}`}
    >
      {value || '-'}
    </Typography>
  </div>
);

interface CardSectionProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * CardSection - A reusable component for grouping related fields
 */
const CardSection: React.FC<CardSectionProps> = ({ children, className }) => (
  <ContactCard className={`claim-section-card ${className || ''}`}>
    {children}
  </ContactCard>
);

interface FieldRowProps {
  children: React.ReactNode;
  className?: string;
}

const FieldRow: React.FC<FieldRowProps> = ({ children, className }) => (
  <div className={`insured-details-row ${className || ''}`}>{children}</div>
);

interface AccordionSectionProps {
  title: string;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  children: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  expanded,
  onChange,
  children,
}) => (
  <Accordion expanded={expanded} onChange={onChange}>
    <AccordionSummary>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        <Typography variant="h5" className="box-title-overview">
          {title}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails className="accordion-details-style-overview">
      {children}
    </AccordionDetails>
  </Accordion>
);
